import * as Sentry from '@sentry/react';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import {
  type FirmwareRelease,
  type GetFirmwareReleasesParams,
  type GetFirmwareReleasesResponse,
  getFirmwareReleases,
} from 'api/admin/firmware/firmware';

type UseFirmwareReleasesParams = Pick<
  GetFirmwareReleasesParams,
  'page' | 'limit' | 'includeArchived' | 'sortBy' | 'sortOrder'
>;

type UseFirmwareReleasesResult = {
  releases: FirmwareRelease[];
  total: number;
  totalPages: number;
  currentPage: number;
};

const errorHandler = (error: unknown) => {
  Sentry.captureException(error);
};

export const useFirmwareReleases = ({
  page,
  limit,
  includeArchived,
  sortBy,
  sortOrder,
}: UseFirmwareReleasesParams): UseQueryResult<UseFirmwareReleasesResult, unknown> => {
  return useQuery({
    queryKey: [
      'firmwareReleases',
      page,
      limit,
      includeArchived ?? false,
      sortBy ?? null,
      sortOrder ?? null,
    ],
    queryFn: async () => {
      const { data } = (await getFirmwareReleases({
        page,
        limit,
        includeArchived,
        sortBy,
        sortOrder,
      })) as AxiosResponse<GetFirmwareReleasesResponse>;

      return {
        releases: data.releases,
        total: data.total,
        totalPages: data.totalPages,
        currentPage: data.currentPage,
      };
    },
    onError: errorHandler,
    retry: 1,
    keepPreviousData: true,
  });
};
